import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { ContentList } from "../components/ContentList";
import { ConfirmDialog } from "../components/ConfirmDialog";
const VIEWS = [
  ["tutti", "Tutti"], ["INDICIZZATO", "Indicizzati"], ["NON_PERTINENTE", "Non pertinenti"],
  ["in_lavorazione", "In lavorazione"], ["ERRORE", "Errori"],
];

export default function Contents({ embedded = false }) {
  const [result, setResult] = useState(null);
  const [view, setView] = useState("tutti");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    try {
      const { data } = await api.get("/contents", { params: { page, page_size: pageSize, stato: view } });
      if (request !== requestRef.current) return;
      setResult(data);
      setPage(data.page);
      setFailed(false);
    } catch (e) {
      if (request !== requestRef.current) return;
      setFailed(true);
      toast.error(errorMessage(e));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [page, pageSize, view]);

  useEffect(() => {
    setLoading(true);
    load();
    return () => { requestRef.current += 1; };
  }, [load]);

  useEffect(() => {
    if (!result?.counts.in_lavorazione) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [result?.counts.in_lavorazione, load]);

  const changeView = (value) => { if (value === view) return; setLoading(true); setView(value); setPage(1); };
  const changePage = (value) => { setLoading(true); setPage(value); };
  const remove = async () => {
    try {
      await api.delete(`/contents/${deleting.id}`);
      await load();
      toast.success("Contenuto eliminato");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="contents-page" className={embedded ? "space-y-4" : "space-y-8"}>
      {!embedded && (
      <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Archivio</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Contenuti</h1>
          <p data-testid="contents-total" className="mt-2 text-sm text-muted-foreground">{result ? `${result.counts.tutti} contenuti caricati` : "Caricamento archivio…"}</p>
        </div>
        <Link to="/aggiungi" data-testid="contents-add-btn" className="inline-flex items-center gap-2 self-start rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8]">
          <Plus className="h-4 w-4" /> Aggiungi contenuto
        </Link>
      </div>
      )}
      <div className="space-y-4">
        <div role="tablist" aria-label="Stato dei contenuti" className="flex flex-wrap gap-2">
          {VIEWS.map(([value, label]) => (
            <button key={value} role="tab" aria-selected={view === value} data-testid={`contents-tab-${value.toLowerCase().replaceAll("_", "-")}`} onClick={() => changeView(value)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${view === value ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground hover:border-primary/50 hover:text-white"}`}>
              {label}<span data-testid={`contents-count-${value.toLowerCase().replaceAll("_", "-")}`} className="font-mono text-xs opacity-75">{result?.counts[value] ?? "—"}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <p data-testid="contents-range">{!loading && result ? (result.total ? `${(result.page - 1) * result.page_size + 1}–${Math.min(result.page * result.page_size, result.total)} di ${result.total}` : "Nessun contenuto in questa vista") : "Caricamento…"}</p>
          <label className="flex items-center gap-2">Per pagina
            <select data-testid="contents-page-size" value={pageSize} onChange={(e) => { setLoading(true); setPageSize(Number(e.target.value)); setPage(1); }} className="rounded-lg border border-border bg-background px-3 py-2 text-foreground">
              {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        {loading ? <div data-testid="contents-loading" className="surface flex items-center gap-2 p-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento contenuti…</div> : failed ? (
          <div data-testid="contents-error" role="alert" className="surface p-6 text-sm">Impossibile aggiornare i contenuti. <button data-testid="contents-retry" onClick={load} className="ml-2 text-primary underline">Riprova</button></div>
        ) : <ContentList items={result?.items || []} onDelete={setDeleting} emptyText="Nessun contenuto in questa vista." />}
        <nav data-testid="contents-pagination" aria-label="Pagine contenuti" className="flex items-center justify-between gap-3 border-t border-border pt-4">
          <button data-testid="contents-prev-page" disabled={loading || page <= 1} onClick={() => changePage(page - 1)} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Precedente</button>
          <span data-testid="contents-page-info" className="text-center text-xs font-mono text-muted-foreground">Pagina {result?.page || 1} di {result?.pages || 1}</span>
          <button data-testid="contents-next-page" disabled={loading || !result || page >= result.pages} onClick={() => changePage(page + 1)} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-40">Successiva <ChevronRight className="h-4 w-4" /></button>
        </nav>
      </div>
      <ConfirmDialog
        open={!!deleting}
        title="Eliminare questo contenuto?"
        description="Verranno eliminate anche tutte le conoscenze estratte da questo contenuto."
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      />
    </div>
  );
}
